import React, { Component } from 'react';
import { twMerge } from 'tailwind-merge';

import Collapse from '../../../../common/components/Collapse/Collapse';
import ArrowIcon from './ArrowIcon';

import './CollapsibleCheckboxFilter.less';

interface Props {
  options: Option[];
  handleFilterChanged: (optionName, checked) => void;
  showOptionsOnPageLoad?: boolean;
  groupName?: string;
  name: string;
  checkedValues: number[];
  invalidSelectionMessage: string;
  isCollapsed?: boolean;
  toggleCollapsed: () => void;
  groupId?: string;
}

type Option = Partial<{
  value: string;
  label: string;
  name: string;
  categoryId: number;
}>;

export default class CollapsibleCheckboxFilter extends Component<Props> {
  static defaultProps = {
    checkedValues: [],
    invalidSelectionMessage: null,
  };

  render() {
    const {
      groupId,
      options,
      name,
      groupName,
      checkedValues,
      isCollapsed,
      toggleCollapsed,
    } = this.props;

    const ariaControlId = `filter-control-${groupId || groupName}`;
    const ariaLabelId = `filter-heading-${this.getHtmlSafeName(name)}`;

    return (
      <div
        id={`filter-group-${groupId || groupName}`}
        className={
          isCollapsed || this.props.invalidSelectionMessage
            ? 'collapsed'
            : 'expanded'
        }
      >
        <div
          onClick={toggleCollapsed}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleCollapsed();
              e.preventDefault();
            }
          }}
          aria-controls={
            this.props.invalidSelectionMessage ? undefined : ariaControlId
          }
          role={this.props.invalidSelectionMessage ? 'presentation' : 'button'}
          aria-expanded={
            this.props.invalidSelectionMessage ? undefined : !isCollapsed
          }
          tabIndex={0}
        >
          <h3
            className={twMerge(
              this.getHeadingClassName(),
              checkedValues.length === 0 && isCollapsed ? 'flex' : 'block'
            )}
            id={ariaLabelId}
          >
            {name}
            <ArrowIcon
              isCollapsed={isCollapsed}
              invalidSelectionMessage={this.props.invalidSelectionMessage}
            />
          </h3>
        </div>

        {this.renderInvalidSelectionMessage() || (
          <>
            <Collapse isCollapsed={isCollapsed} controlId={ariaControlId}>
              <fieldset
                aria-labelledby={ariaLabelId}
                className={`collapsible-checkbox-filter-wrapper ${
                  isCollapsed ? 'invisible' : ''
                }`}
              >
                {options.map((option) => {
                  const isChecked = checkedValues.includes(
                    this.sanitizeOptionValue(option.value)
                  );
                  const htmlSafeName = this.getHtmlSafeName(
                    `${name}-${option.value}`
                  );
                  return (
                    <div className="checkbox-spacing" key={option.value}>
                      <input
                        type="checkbox"
                        id={htmlSafeName}
                        name={option.name || name}
                        checked={isChecked}
                        onChange={(e) =>
                          this.handleCheckboxChanged(
                            this.sanitizeOptionValue(option.value),
                            e.currentTarget.checked
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.handleCheckboxChanged(
                              this.sanitizeOptionValue(option.value),
                              !isChecked
                            );
                          }
                        }}
                      />
                      <label
                        className={this.getCheckboxLabelClassName(isChecked)}
                        htmlFor={htmlSafeName}
                      >
                        <span>{option.label}</span>
                      </label>
                    </div>
                  );
                })}
              </fieldset>
            </Collapse>

            {isCollapsed && checkedValues.length > 0 && (
              <div className="collapsible-filter-selected-option">
                {options
                  .filter((option) =>
                    checkedValues.includes(
                      this.sanitizeOptionValue(option.value)
                    )
                  )
                  .map((option) => {
                    return (
                      <React.Fragment key={option.value}>
                        {option.label}
                        <br />
                      </React.Fragment>
                    );
                  })}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  getHeadingClassName() {
    return twMerge(
      'heading',
      this.props.invalidSelectionMessage !== null && 'invalid'
    );
  }

  getCheckboxLabelClassName(isChecked) {
    return twMerge('checkbox-label', isChecked && 'selected');
  }

  renderInvalidSelectionMessage() {
    if (!this.props.invalidSelectionMessage) {
      return null;
    }

    return (
      <span className="invalid-message" tabIndex={0}>
        {this.props.invalidSelectionMessage}
      </span>
    );
  }

  sanitizeOptionValue(value) {
    return Number.isInteger(parseInt(value)) ? parseInt(value) : value;
  }

  handleCheckboxChanged = (optionName, checked) => {
    const { handleFilterChanged, groupName } = this.props;

    if (groupName) {
      this.handleGroupFilterUpdate(optionName, checked);
    } else {
      handleFilterChanged(optionName, checked);
    }
  };

  handleGroupFilterUpdate(optionName, checked) {
    const { checkedValues, handleFilterChanged, groupName } = this.props;

    // clone checkedValues
    let selectedValues = checkedValues.slice(0);

    if (checked) {
      selectedValues.push(optionName);
    } else {
      selectedValues = selectedValues.filter(
        (selectedValue) => selectedValue !== optionName
      );
    }

    handleFilterChanged(groupName, selectedValues);
  }

  getHtmlSafeName(rawName) {
    return rawName.replace(/[^\w-]/g, '');
  }
}
